import { useMemo, useRef } from "react";  
import { useFootnoteHooks } from "src/DigitalProtocolModule/hooks";  
import { FOOTNOTE_GROUP } from "src/DigitalProtocolModule/constants";  
import { fetchHistory } from "src/actions/historyTrial";  
import { useSelector } from "react-redux";  
import DOMPurify from "dompurify";
import styles from './index.module.scss';  
import './index.scss'
  
export function ViewFootnotes() {  
  const footnoteConfigState = useSelector(fetchHistory).data.dpFootnoteConfig;  
  const { getFootnoteStateMergedWithSoftSave, checkIfExistFootnoteWarning } = useFootnoteHooks();  
  const footnoteMergedDataState = getFootnoteStateMergedWithSoftSave();  

  const serialNumRef = useRef<any>();
  serialNumRef.current = 0;

  const existFootnoteWarning = useMemo(
    () => checkIfExistFootnoteWarning(footnoteMergedDataState),
    [footnoteMergedDataState]
  );

  const getSortedFootnotes = (group, sortKey) => {  
    if (!footnoteMergedDataState) return [];  
    const footnoteTypeMap = footnoteConfigState.footnoteTypeList.reduce((map, typeItem) => {  
      map[typeItem.footnote_type_id] = typeItem.footnote_type;  
      return map;  
    }, {});  
    
    return footnoteMergedDataState  
      .filter(item => item.footnote_group === group)  
      .map(item => ({  
        ...item,  
        footnote_type: footnoteTypeMap[item.footnote_type_id],  
      }))  
      .sort((a, b) => a[sortKey] - b[sortKey]);  
  };  
    
  const groupFootnotes = (footnotes, groupKeyFn) => {  
    return footnotes.reduce((acc, item) => {  
      const groupKey = groupKeyFn(item);  
      const existingEntry = acc.find(entry => entry.groupKey === groupKey);  
     
      if (existingEntry) {  
        existingEntry.footnote_preview_text.push(item.footnote_preview_text);  
      } else {  
        acc.push({ ...item, footnote_preview_text: [item.footnote_preview_text], groupKey });  
      }  
        
      return acc;  
    }, []).map(({ groupKey, ...rest }) => rest);
  };  
    
  const groupByVisitAndPeriodId = footnotes =>   
    groupFootnotes(footnotes, item => `${item.visit}`);  
    
  const groupByActivityId = footnotes =>   
    groupFootnotes(footnotes, item => item.activity_id);  
    
  const groupByCellId = footnotes =>   
    groupFootnotes(footnotes, item => `${item.visit}-${item.activity_id}`);  
    
  const groupByPeriodId = footnotes =>   
    groupFootnotes(footnotes, item => item.period_id);  
    
  const footnoteLists = useMemo(() => ({  
    Visit: groupByVisitAndPeriodId(getSortedFootnotes(FOOTNOTE_GROUP.VISIT, 'visit')),  
    Activity: groupByActivityId(getSortedFootnotes(FOOTNOTE_GROUP.ACTIVITY, 'activity_id')),  
    Cell: groupByCellId(getSortedFootnotes(FOOTNOTE_GROUP.CELL, 'activity_id').sort((a, b) => {  
      if (a.activity_id !== b.activity_id) return a.activity_id - b.activity_id;  
      return a.visit - b.visit;  
    })),  
    Period: groupByPeriodId(getSortedFootnotes(FOOTNOTE_GROUP.PERIOD, 'period_id')),  
  }), [footnoteConfigState, footnoteMergedDataState]);  
  
  const getFootnotesHeaderByGroup = (group, footnote) => {  
    if (!footnote) return '';  
    const headers = {  
      Visit: `Visit ${footnote.visit}`,  
      Activity: `${footnote.std_activity}`,  
      Cell: `${footnote.std_activity}, Visit ${footnote.visit}`,  
      Period: `${footnote.period_name}`  
    };  
    return headers[group] || '';  
  };  
  
  return (  
    <div className={`dp-soa-footnotes ${styles.footnotes}`}>  
      <h1>Footnotes</h1>  
      {existFootnoteWarning && (
        <div className={styles.errorMsg}>Your recent changes have impacted some footnotes at the activity or cell level. Please review and update them accordingly.</div>
      )}
      {Object.entries(FOOTNOTE_GROUP).map(([footnote_type_key, footnote_type_value]) => (  
        <ul key={footnote_type_key} className={`${styles.footnoteList} ${styles[footnote_type_value]}`}>  
          <span className={styles.footnote_type}>{`${footnote_type_key}-LEVEL FOOTNOTES`}</span>  
          {footnoteLists[footnote_type_value]?.length > 0 ? (  
            footnoteLists[footnote_type_value].map((footnote, idx) => (  
                <li key={footnote.footnote_id}>  
                <span className={`${styles.types} ${styles.container} ${styles[footnote_type_value]}`}>  
                    {getFootnotesHeaderByGroup(footnote_type_value, footnote)}  
                </span>  
                <br />  
                <span className={`${styles.notes} ${styles.container}`}>Notes:</span>  
                <br />  
                {footnote.footnote_preview_text.map((k, index) => {
                  serialNumRef.current += 1;
                  return (
                    <div className={styles.listItem} key={index}
                      dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(`${serialNumRef.current}.${k}`)
                    }}></div>
                  )
                })}
                </li>  
            ))  
            ) : (  
            <span key={footnote_type_value} className={styles.empty_container}>{`No ${footnote_type_value?.toLowerCase()} level footnotes defined.`}</span>  
            )}  
        </ul>  
      ))}  
    </div>  
  );  
}  
